import React, { useEffect, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  MenuItem,
  Slide,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  AddOutlined,
  Close,
  CloudUploadOutlined,
  DeleteOutline,
  SaveOutlined,
} from '@mui/icons-material';
import { fetchData, postData } from '../../helpers/apiUtils';
import { HubSite } from './types';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface CriteriaItem {
  heading: string;
  text: string;
}
interface FaqItem {
  q: string;
  a: string;
}
/** Broker-spezifischer Hinweis unter der Tabelle — je Eintrag ein eigener Absatz, Label fett. */
interface BrokerNotice {
  label: string;
  text: string;
}
export interface OverviewText {
  h1?: string;
  h2?: string;
  lead?: string;
  affiliate_disclaimer?: string;
  sources?: string;
  hinweis?: string;
  definition?: { title?: string; intro?: string; highlight?: string; body?: string[] };
  criteria?: { title?: string; items?: CriteriaItem[] };
  faq?: { title?: string; items?: FaqItem[] };
  broker_notices?: BrokerNotice[];
}

interface Props {
  open: boolean;
  sites: HubSite[];
  onClose: () => void;
}

const S = { mb: 2 };

/**
 * Editor for the /broker overview editorial text (H1, lead, "Online Broker Definition", the 6
 * selection criteria, the 6-item FAQ). A "Basis"-scope edits the ONE shared copy pushed to every
 * portal; selecting a portal edits a SPARSE override for it (fill only the fields that should differ,
 * e.g. the H1 — an empty field inherits the base). "An alle Portale ausspielen" deploys base + each
 * portal's override, merged per portal. A field left empty at BOTH levels keeps the portal theme's
 * own hardcoded wording.
 */
const OverviewTextDialog: React.FC<Props> = ({ open, sites, onClose }) => {
  // '' = shared base; otherwise a site_key whose sparse override is being edited.
  const [scope, setScope] = useState('');
  const [data, setData] = useState<OverviewText>({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Tracks unsaved edits so switching the Bereich scope can warn before discarding them.
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState<{
    severity: 'success' | 'warning' | 'error';
    text: string;
  } | null>(null);

  // Always open on the Basis scope.
  useEffect(() => {
    if (open) setScope('');
  }, [open]);

  const scopeUrl = (s: string) =>
    s === ''
      ? '/api/broker-hub/settings/overview-text'
      : `/api/broker-hub/sites/${s}/overview-text-override`;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setMessage(null);
    setLoading(true);
    fetchData<OverviewText>(scopeUrl(scope)).then((r) => {
      if (cancelled) return;
      setData(r.success && r.data ? r.data : {});
      setDirty(false);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, scope]);

  // Every editor mutation routes through `edit` so `dirty` is set — a scope switch can then warn.
  const edit = (updater: (prev: OverviewText) => OverviewText) => {
    setDirty(true);
    setData(updater);
  };
  const set = (patch: Partial<OverviewText>) => edit((prev) => ({ ...prev, ...patch }));
  const setDef = (patch: Partial<NonNullable<OverviewText['definition']>>) =>
    edit((prev) => ({ ...prev, definition: { ...prev.definition, ...patch } }));

  const save = async (): Promise<boolean> => {
    setBusy(true);
    setMessage(null);
    const res = await fetchData(scopeUrl(scope), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    setBusy(false);
    if (!res.success) {
      setMessage({ severity: 'error', text: res.error || 'Speichern fehlgeschlagen' });
      return false;
    }
    setDirty(false);
    setMessage({ severity: 'success', text: 'Gespeichert.' });
    return true;
  };

  const deployAll = async () => {
    if (!(await save())) return;
    setBusy(true);
    const res = await postData<Record<string, { ok: boolean; error?: string }>>(
      '/api/broker-hub/settings/deploy-overview-text',
      {},
    );
    setBusy(false);
    const results = (res.data ?? {}) as Record<string, { ok: boolean; error?: string }>;
    const entries = Object.entries(results);
    const failed = entries.filter(([, r]) => !r.ok);
    if (failed.length === 0 && entries.length > 0) {
      setMessage({
        severity: 'success',
        text: `Auf alle ${entries.length} Portale ausgespielt.`,
      });
    } else {
      setMessage({
        severity: failed.length === entries.length ? 'error' : 'warning',
        text:
          `Ausgespielt: ${
            entries
              .filter(([, r]) => r.ok)
              .map(([k]) => k)
              .join(', ') || '—'
          }. ` + `Fehler: ${failed.map(([k, r]) => `${k} (${r.error})`).join('; ') || '—'}`,
      });
    }
  };

  // ---- criteria / faq list helpers ----
  const criteria = data.criteria?.items ?? [];
  const setCriteria = (items: CriteriaItem[]) =>
    edit((prev) => ({ ...prev, criteria: { ...prev.criteria, items } }));
  const faq = data.faq?.items ?? [];
  const setFaq = (items: FaqItem[]) => edit((prev) => ({ ...prev, faq: { ...prev.faq, items } }));
  const brokerNotices = data.broker_notices ?? [];
  const setBrokerNotices = (items: BrokerNotice[]) =>
    edit((prev) => ({ ...prev, broker_notices: items }));
  const body = data.definition?.body ?? [];
  const setBody = (b: string[]) => setDef({ body: b });

  const field = (
    label: string,
    value: string | undefined,
    onChange: (v: string) => void,
    multiline = false,
  ) => (
    <TextField
      label={label}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      fullWidth
      multiline={multiline}
      minRows={multiline ? 2 : undefined}
      sx={S}
    />
  );

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6">
            Broker-Übersichtstexte (alle Portale)
          </Typography>
          <Button
            color="inherit"
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
            onClick={save}
            disabled={busy || loading}
          >
            Speichern
          </Button>
          <Button
            color="inherit"
            startIcon={<CloudUploadOutlined />}
            onClick={deployAll}
            disabled={busy || loading}
            sx={{ ml: 1 }}
          >
            An alle Portale ausspielen
          </Button>
          <IconButton color="inherit" onClick={onClose} sx={{ ml: 1 }}>
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box p={3} sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
        {message && (
          <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}
        <TextField
          select
          size="small"
          label="Bereich"
          value={scope}
          onChange={(e) => {
            // Guard against silently discarding unsaved edits when switching scope.
            if (dirty && !window.confirm('Ungespeicherte Änderungen verwerfen?')) return;
            setScope(e.target.value);
          }}
          sx={{ mb: 2, minWidth: 300 }}
          disabled={busy}
        >
          <MenuItem value="">Basis (alle Portale)</MenuItem>
          {sites.map((s) => (
            <MenuItem key={s.site_key} value={s.site_key}>
              Nur {s.name || s.site_key.toUpperCase()}
            </MenuItem>
          ))}
        </TextField>
        <Alert severity="info" sx={{ mb: 3 }}>
          {scope === '' ? (
            <>
              <strong>Basis-Text</strong> für die /broker-Übersichtsseite{' '}
              <strong>aller Portale</strong>. Ein leeres Feld behält den bestehenden Text des
              jeweiligen Portals.
            </>
          ) : (
            <>
              <strong>Nur {sites.find((s) => s.site_key === scope)?.name ?? scope}</strong>: nur
              ausgefüllte Felder überschreiben hier den Basis-Text — leer = Basis übernehmen.
            </>
          )}{' '}
          Nach dem Speichern <strong>„An alle Portale ausspielen“</strong> klicken (verteilt Basis +
          Portal-Overrides).
        </Alert>

        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              Kopfbereich
            </Typography>
            {field('Überschrift (H1)', data.h1, (v) => set({ h1: v }))}
            {field('Unterüberschrift (H2)', data.h2, (v) => set({ h2: v }))}
            {field('Einleitungstext', data.lead, (v) => set({ lead: v }), true)}

            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Hinweise / Fußzeile
            </Typography>
            {field(
              'Affiliate-Hinweis',
              data.affiliate_disclaimer,
              (v) => set({ affiliate_disclaimer: v }),
              true,
            )}
            {field('Quellen', data.sources, (v) => set({ sources: v }))}
            {field('Gewähr-Hinweis', data.hinweis, (v) => set({ hinweis: v }), true)}

            <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 0.5 }}>
              Broker-Hinweise
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Je Eintrag ein eigener Absatz unter dem Gewähr-Hinweis, das Label erscheint fett. Für
              vom Broker vorgeschriebene Texte. Das Label wird wörtlich ausgegeben — den Doppelpunkt
              also mit eintippen, z. B. „Risikohinweis Plus500:“. Im Bereich „Basis“ gilt die Liste
              für alle Portale. Ein Portal kann sie nur abschalten, indem es hier einen eigenen
              Eintrag <em>ohne Text</em> anlegt — eine leere Liste übernimmt wieder die Basis.
            </Typography>
            {brokerNotices.map((n, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: order-stable list
              <Box key={i} display="flex" gap={1} alignItems="flex-start" sx={{ mb: 1 }}>
                <Box flex={1}>
                  <TextField
                    label="Label (fett)"
                    value={n.label}
                    onChange={(e) =>
                      setBrokerNotices(
                        brokerNotices.map((x, j) =>
                          j === i ? { ...x, label: e.target.value } : x,
                        ),
                      )
                    }
                    size="small"
                    fullWidth
                    sx={{ mb: 0.5 }}
                  />
                  <TextField
                    label="Text"
                    value={n.text}
                    onChange={(e) =>
                      setBrokerNotices(
                        brokerNotices.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)),
                      )
                    }
                    size="small"
                    fullWidth
                    multiline
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setBrokerNotices(brokerNotices.filter((_, j) => j !== i))}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddOutlined />}
              onClick={() => setBrokerNotices([...brokerNotices, { label: '', text: '' }])}
            >
              Hinweis hinzufügen
            </Button>

            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Abschnitt „Online Broker Definition“
            </Typography>
            {field('Titel', data.definition?.title, (v) => setDef({ title: v }))}
            {field('Einleitung', data.definition?.intro, (v) => setDef({ intro: v }), true)}
            {field(
              'Hervorhebung (💡 Wichtig-Box)',
              data.definition?.highlight,
              (v) => setDef({ highlight: v }),
              true,
            )}
            <Typography variant="body2" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>
              Weitere Absätze
            </Typography>
            {body.map((p, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: order-stable list
              <Box key={i} display="flex" gap={1} sx={{ mb: 1 }}>
                <TextField
                  value={p}
                  onChange={(e) => setBody(body.map((x, j) => (j === i ? e.target.value : x)))}
                  size="small"
                  fullWidth
                  multiline
                />
                <IconButton size="small" onClick={() => setBody(body.filter((_, j) => j !== i))}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button size="small" startIcon={<AddOutlined />} onClick={() => setBody([...body, ''])}>
              Absatz hinzufügen
            </Button>

            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Abschnitt „Darauf ist zu achten“
            </Typography>
            {field('Titel', data.criteria?.title, (v) =>
              edit((prev) => ({ ...prev, criteria: { ...prev.criteria, title: v } })),
            )}
            {criteria.map((c, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: order-stable list
              <Box key={i} display="flex" gap={1} alignItems="flex-start" sx={{ mb: 1 }}>
                <Box flex={1}>
                  <TextField
                    label="Überschrift"
                    value={c.heading}
                    onChange={(e) =>
                      setCriteria(
                        criteria.map((x, j) => (j === i ? { ...x, heading: e.target.value } : x)),
                      )
                    }
                    size="small"
                    fullWidth
                    sx={{ mb: 0.5 }}
                  />
                  <TextField
                    label="Text"
                    value={c.text}
                    onChange={(e) =>
                      setCriteria(
                        criteria.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)),
                      )
                    }
                    size="small"
                    fullWidth
                    multiline
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setCriteria(criteria.filter((_, j) => j !== i))}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddOutlined />}
              onClick={() => setCriteria([...criteria, { heading: '', text: '' }])}
            >
              Kriterium hinzufügen
            </Button>

            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Abschnitt „Häufig gestellte Fragen“
            </Typography>
            {field('Titel', data.faq?.title, (v) =>
              edit((prev) => ({ ...prev, faq: { ...prev.faq, title: v } })),
            )}
            {faq.map((f, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: order-stable list
              <Box key={i} display="flex" gap={1} alignItems="flex-start" sx={{ mb: 1 }}>
                <Box flex={1}>
                  <TextField
                    label="Frage"
                    value={f.q}
                    onChange={(e) =>
                      setFaq(faq.map((x, j) => (j === i ? { ...x, q: e.target.value } : x)))
                    }
                    size="small"
                    fullWidth
                    sx={{ mb: 0.5 }}
                  />
                  <TextField
                    label="Antwort"
                    value={f.a}
                    onChange={(e) =>
                      setFaq(faq.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)))
                    }
                    size="small"
                    fullWidth
                    multiline
                  />
                </Box>
                <IconButton size="small" onClick={() => setFaq(faq.filter((_, j) => j !== i))}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddOutlined />}
              onClick={() => setFaq([...faq, { q: '', a: '' }])}
              sx={{ mb: 4 }}
            >
              Frage hinzufügen
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default OverviewTextDialog;
